import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Textarea,
  IconButton,
  Button,
  Code,
  FormLabel,
  Tag
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { LuBraces, LuCode, LuEye } from 'react-icons/lu';

// Parse a JSON string into a flat array of field rows
function parseJSONToFields(jsonString) {
  try {
    const parsed = JSON.parse(jsonString);
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) return null;
    return Object.entries(parsed).map(([key, val], idx) => {
      let type = typeof val;
      if (Array.isArray(val)) type = 'array';
      else if (val === null) type = 'string';
      const isVariable = typeof val === 'string' && val.includes('{{') && val.includes('}}');
      return {
        id: String(idx + 1),
        key,
        value: typeof val === 'object' ? JSON.stringify(val) : String(val),
        type: isVariable ? 'variable' : type,
      };
    });
  } catch {
    return null;
  }
}

// Convert field rows into a JSON string
function fieldsToJSON(fields) {
  const obj = {};
  fields.forEach((f) => {
    if (!f.key) return;
    const isVar = f.value.includes('{{') && f.value.includes('}}');
    if (isVar) {
      obj[f.key] = f.value;
    } else {
      switch (f.type) {
        case 'number':
          obj[f.key] = parseFloat(f.value) || 0;
          break;
        case 'boolean':
          obj[f.key] = f.value === 'true';
          break;
        case 'object':
        case 'array':
          try { obj[f.key] = JSON.parse(f.value); } catch { obj[f.key] = f.value; }
          break;
        default:
          obj[f.key] = f.value;
      }
    }
  });
  return JSON.stringify(obj, null, 2);
}

export default function JSONBodyBuilder({ value, onChange, onOpenVariablePicker }) {
  const [mode, setMode] = useState('visual');
  const [fields, setFields] = useState(() => {
    const parsed = parseJSONToFields(value);
    return parsed && parsed.length > 0 ? parsed : [{ id: '1', key: '', value: '', type: 'string' }];
  });

  // When switching to visual, parse existing raw JSON into fields
  const switchMode = (newMode) => {
    if (newMode === 'visual' && mode === 'raw') {
      const parsed = parseJSONToFields(value);
      if (parsed) setFields(parsed);
    }
    setMode(newMode);
  };

  // When fields change, emit updated JSON
  const applyFields = (newFields) => {
    setFields(newFields);
    onChange(fieldsToJSON(newFields));
  };

  const addField = () =>
    applyFields([...fields, { id: String(Date.now()), key: '', value: '', type: 'string' }]);

  const removeField = (id) => applyFields(fields.filter((f) => f.id !== id));

  const updateField = (id, updates) =>
    applyFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)));

  const handleInsertVariable = (fieldId) => {
    onOpenVariablePicker((variable) => {
      updateField(fieldId, { value: variable, type: 'variable' });
    });
  };

  const previewJSON = fieldsToJSON(fields);

  return (
    <VStack spacing={3} align="stretch">
      {/* Mode Toggle */}
      <HStack spacing={2}>
        <Button
          size="xs"
          variant={mode === 'visual' ? 'solid' : 'outline'}
          bg={mode === 'visual' ? '#155dfc' : 'gray.100'}
          color={mode==='visual'?"white":"black"}
          leftIcon={<LuEye size={12} />}
          onClick={() => switchMode('visual')}
        >
          Visual Builder
        </Button>
        <Button
          size="xs"
          variant={mode === 'raw' ? 'solid' : 'outline'}
          bg={mode === 'raw' ? '#155dfc' : 'gray.100'}
          color={mode==='raw'?"white":"black"}

          leftIcon={<LuCode size={12} />}
          onClick={() => switchMode('raw')}
        >
          Raw JSON
        </Button>
      </HStack>

      {/* ── Visual Builder ── */}
      {mode === 'visual' && (
        <VStack spacing={3} align="stretch">
          <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            {/* Header row */}
            <HStack justify="space-between" mb={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                JSON Fields
              </Text>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="blue"
                leftIcon={<AddIcon boxSize="9px" />}
                onClick={addField}
              >
                Add Field
              </Button>
            </HStack>

            <VStack spacing={2} align="stretch">
              {fields.map((field) => (
                <Box
                  key={field.id}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                >
                  <HStack spacing={2} align="flex-start">
                    {/* Key */}
                    <Input
                      size="xs"
                      placeholder="Key"
                      value={field.key}
                      onChange={(e) => updateField(field.id, { key: e.target.value })}
                      flex="2"
                      fontFamily="mono"
                    />

                    {/* Type */}
                    <Select
                      size="xs"
                      value={field.type}
                      onChange={(e) => updateField(field.id, { type: e.target.value })}
                      flex="2"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="variable">Variable</option>
                      <option value="object">Object</option>
                      <option value="array">Array</option>
                    </Select>

                    {/* Value + variable picker */}
                    <Box flex="3" position="relative">
                      <Input
                        size="xs"
                        placeholder='Value or {{variable}}'
                        value={field.value}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        fontFamily="mono"
                        pr="7"
                      />
                      <IconButton
                        aria-label="Pick variable"
                        icon={<LuBraces size={12} />}
                        size="xs"
                        variant="ghost"
                        color="var(--brand-primary)"
                        _hover={{ bg: 'blue.50' }}
                        position="absolute"
                        right={0}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={1}
                        h="100%"
                        minW="24px"
                        onClick={() => handleInsertVariable(field.id)}
                      />
                    </Box>

                    {/* Remove */}
                    <IconButton
                      aria-label="Remove field"
                      icon={<DeleteIcon boxSize="11px" />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeField(field.id)}
                    />
                  </HStack>

                  {/* Variable badge */}
                  {field.value.includes('{{') && field.value.includes('}}') && (
                    <Box mt={1.5} pt={1.5} borderTop="1px solid" borderColor="gray.100">
                      <Tag variant="subtle">
                        Dynamic Variable
                      </Tag>
                    </Box>
                  )}
                </Box>
              ))}

              {fields.length === 0 && (
                <Box textAlign="center" py={4} border="2px dashed" borderColor="gray.200" borderRadius="md">
                  <Text fontSize="xs" color="gray.400">
                    No fields. Click "Add Field" to build your JSON payload.
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>

          {/* JSON Preview */}
          <Box bg="gray.800" borderRadius="md" p={3}>
            <HStack spacing={2} mb={2}>
              <LuCode size={12} color="#9CA3AF" />
              <Text fontSize="xs" fontWeight="semibold" color="gray.300">
                JSON Preview
              </Text>
            </HStack>
            <Box
              as="pre"
              fontSize="xs"
              color="gray.300"
              fontFamily="mono"
              overflowX="auto"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
            >
              {previewJSON || '{}'}
            </Box>
          </Box>
        </VStack>
      )}

      {/* ── Raw JSON ── */}
      {mode === 'raw' && (
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between">
            <FormLabel fontSize="xs" mb={0}>Raw JSON Editor</FormLabel>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="blue"
              leftIcon={<LuBraces size={11} />}
              onClick={() => onOpenVariablePicker((variable) => onChange((value || '') + variable))}
            >
              Insert Variable
            </Button>
          </HStack>
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={'{\n  "contact_no": "{{customer.contact}}",\n  "order_id": "{{event.props.order_id}}"\n}'}
            fontFamily="mono"
            fontSize="xs"
            rows={10}
            bg="gray.800"
            color="gray.100"
            borderColor="gray.600"
            _placeholder={{ color: 'gray.500' }}
            spellCheck={false}
          />
          <HStack
            spacing={2}
            p={2}
            bg="blue.50"
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
            align="flex-start"
          >
            <Box pt="1px" flexShrink={0}>
              <LuBraces size={13} color="var(--brand-primary)" />
            </Box>
            <Text fontSize="xs" color="blue.800">
              Use <Code fontSize="xs">{`{{variable}}`}</Code> syntax for dynamic values resolved at runtime.
            </Text>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
}
